import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootStoreContext } from '../../global/storeContext';
import StringUtils from '../../utils/string';
import { AdminDrawer, IAnchorType } from '../uiComponent/drawer';
import { UserListUser } from '../../utils/apiClients';

export const Users: React.FC = observer(() => {
    const { dashboardStore, uiStore } = React.useContext(RootStoreContext);
    const [cursor, setCursor] = React.useState<string | undefined>(undefined);
    const [users, setUsers] = React.useState<UserListUser[]>([]);
    const [selectedUser, setSelectedUser] = React.useState<UserListUser | null>(null);
    const [isUserDrawerOpen, setIsUserDrawerOpen] = React.useState<boolean>(false);

    const getUsers = async () => {
        const data = await dashboardStore.fetchUsers(cursor);
        if (data) {
            let newUsers = data.users;

            if (!!cursor) {
                newUsers = [...users, ...newUsers];
            }

            newUsers = newUsers.sort((a, b) => a.createdAt - b.createdAt);

            setUsers(newUsers);
            setCursor(data.cursor);
        }
    };

    useEffect(() => {
        getUsers();
    }, []);

    const handleUserClick = (user: UserListUser) => {
        setSelectedUser(user);
        setIsUserDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsUserDrawerOpen(false);
        setSelectedUser(null);
    };

    const handlePayout = async (u: UserListUser) => {
        if (!u) return;
        try {
            uiStore.showLoading();
            await dashboardStore.createUserPayout(u.uid);
            // update user account balance
            u.walletBalance = 0;

            uiStore.success('Payout Request Created');
        } catch (error) {
            console.error('Error creating payout:', error);
            uiStore.error(`Error creating payout for ${u.firstname}`);
        } finally {
            uiStore.hideLoading();
        }
    };

    const handleFlagUser = async (user: UserListUser) => {
        try {
            const resp = await dashboardStore.flagUser(user.uid, user.isFlagged ?? false);
            if (resp) {
                const u = users.find((u) => u.uid == user.uid);
                if (u) {
                    u.isFlagged = !user.isFlagged;
                    user.isFlagged = u.isFlagged;
                }
            }
        } catch (error) {
            console.error('Error Flagging user', error);
        }
    };

    return (
        <>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Account Balance</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow key={user.uid} hover onClick={() => handleUserClick(user)}>
                                <TableCell>
                                    {user.firstname} {user.lastname}
                                </TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{StringUtils.asCurrency(user.walletBalance)}</TableCell>
                                <TableCell>
                                    {new Date(user.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePayout(user);
                                        }}
                                        disabled={
                                            !user.walletBalance ||
                                            Number(user.walletBalance) <= 0 ||
                                            uiStore.loading
                                        }
                                    >
                                        Payout
                                    </Button>
                                    &nbsp;
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFlagUser(user);
                                        }}
                                    >
                                        {user.isFlagged ? `UnFlag User` : 'Flag User'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell align='center' colSpan={4}>
                                <Button disabled={!cursor} onClick={getUsers}>
                                    ...Load more
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <AdminDrawer
                type={IAnchorType.RIGHT}
                open={isUserDrawerOpen}
                onClose={handleDrawerClose}
            >
                {selectedUser && (
                    <Box sx={{ p: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                            }}
                        >
                            <Typography variant='h6'>User Details</Typography>
                            <IconButton onClick={handleDrawerClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                            <strong>User ID:</strong> {selectedUser.uid}
                        </Typography>
                        <Typography variant='subtitle1' sx={{ mb: 3 }}>
                            <strong>Created At:</strong>{' '}
                            {new Date(selectedUser.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                            <strong>Name:</strong> {selectedUser.firstname} {selectedUser.lastname}
                        </Typography>
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                            <strong>Email:</strong> {selectedUser.email}
                        </Typography>
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                            <strong>Email Verified:</strong>{' '}
                            {selectedUser.emailVerified ? 'Yes' : 'No'}
                        </Typography>
                        <Typography variant='subtitle1' sx={{ mb: 2 }}>
                            <strong>Account Balance:</strong>{' '}
                            {StringUtils.asCurrency(selectedUser.walletBalance)}
                        </Typography>

                        <Button
                            variant='contained'
                            color='primary'
                            fullWidth
                            onClick={() => handlePayout(selectedUser)}
                            disabled={
                                !selectedUser!.walletBalance ||
                                Number(selectedUser!.walletBalance) <= 0 ||
                                uiStore.loading
                            }
                        >
                            Create Payout
                        </Button>
                    </Box>
                )}
            </AdminDrawer>
        </>
    );
});
